import React, { useState, useEffect } from 'react';
import { User, Briefcase, Target, MessageSquare, Sparkles, FolderOpen, ChevronDown, CheckCircle, X, CreditCard, Check, Copy, Trash2, Search } from 'lucide-react';
import { profileService } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';
import { useLocation, useNavigate } from 'react-router-dom';

// Toast Component
const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    container: {
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
      padding: '1rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      zIndex: 1000,
      animation: 'slideIn 0.3s ease-out',
      minWidth: '300px',
    },
    icon: {
      color: '#10b981',
      flexShrink: 0,
    },
    message: {
      flex: 1,
      fontSize: '0.95rem',
      fontWeight: 500,
      color: '#1e293b',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      color: '#64748b',
      transition: 'color 0.2s',
    },
  };

  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(400px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div style={styles.container}>
        <CheckCircle size={24} style={styles.icon} />
        <span style={styles.message}>{message}</span>
        <button
          onClick={onClose}
          style={styles.closeButton}
          onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
        >
          <X size={20} />
        </button>
      </div>
    </>
  );
};

// Navigation Bar Component
const NavigationBar = ({ activeTab, onTabChange }) => {
  const [hoveredTab, setHoveredTab] = useState(null);

  const styles = {
    nav: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: '0.5rem',
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
    },
    tab: {
      flex: 1,
      padding: '0.875rem 1.5rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      color: '#64748b',
    },
    activeTab: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    hoverTab: {
      background: '#f1f5f9',
    },
  };

  const getTabStyle = (tabName) => {
    const isActive = activeTab === tabName;
    const isHovered = hoveredTab === tabName && !isActive;
    
    return {
      ...styles.tab,
      ...(isActive ? styles.activeTab : {}),
      ...(isHovered ? styles.hoverTab : {}),
    };
  };

  return (
    <nav style={styles.nav}>
      <button
        style={getTabStyle('profile')}
        onClick={() => onTabChange('profile')}
        onMouseEnter={() => setHoveredTab('profile')}
        onMouseLeave={() => setHoveredTab(null)}
      >
        <User size={20} />
        User Profile
      </button>
      <button
        style={getTabStyle('subscription')}
        onClick={() => onTabChange('subscription')}
        onMouseEnter={() => setHoveredTab('subscription')}
        onMouseLeave={() => setHoveredTab(null)}
      >
        <CreditCard size={20} />
        Manage Subscription
      </button>
      <button
        style={getTabStyle('savedmessage')}
        onClick={() => onTabChange('savedmessage')}
        onMouseEnter={() => setHoveredTab('savedmessage')}
        onMouseLeave={() => setHoveredTab(null)}
      >
        <FolderOpen size={20} />
        Saved Messages
      </button>
    </nav>
  );
};

// Modal Component
const MessageModal = ({ message, onClose, onCopy, onDelete, isCopied, loadingContent }) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
    },
    modal: {
      background: 'white',
      borderRadius: '16px',
      maxWidth: '800px',
      width: '100%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    header: {
      padding: '2rem 2rem 1rem 2rem',
      borderBottom: '1px solid #e2e8f0',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '1rem',
    },
    meta: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
    },
    metaBadge: {
      background: '#f1f5f9',
      padding: '0.5rem 1rem',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#475569',
    },
    content: {
      padding: '2rem',
      overflowY: 'auto',
      flex: 1,
      minHeight: '200px',
    },
    messageText: {
      fontSize: '1rem',
      color: '#1e293b',
      lineHeight: 1.8,
      whiteSpace: 'pre-wrap',
      background: '#f8fafc',
      padding: '1.5rem',
      borderRadius: '12px',
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: '1rem',
    },
    button: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    copyButton: {
      background: '#2563eb',
      color: 'white',
    },
    copyButtonCopied: {
      background: '#10b981',
      color: 'white',
    },
    deleteButton: {
      background: '#ef4444',
      color: 'white',
    },
    closeButton: {
      background: '#f1f5f9',
      color: '#64748b',
    },
    closeIcon: {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '8px',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#64748b',
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.overlay}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          style={styles.closeIcon}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e2e8f0';
            e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f1f5f9';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <X size={20} />
        </button>

        <div style={styles.header}>
          <h2 style={styles.title}>{message.title}</h2>
          <div style={styles.meta}>
            <span style={styles.metaBadge}>📧 {message.contact.name}</span>
            <span style={styles.metaBadge}>💬 {message.medium}</span>
            <span style={styles.metaBadge}>📝 {message.message_type.replace('_', ' ')}</span>
            <span style={styles.metaBadge}>
              📅 {new Date(message.created_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </div>

        <div style={styles.content}>
          {loadingContent ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <div style={{ marginBottom: '1rem' }}>Loading message...</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                <motion.div
                  style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#667eea' }}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                />
                <motion.div
                  style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#667eea' }}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div
                  style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#667eea' }}
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                />
              </div>
            </div>
          ) : (
            <div style={styles.messageText}>
              {message.fullContent || message.preview || 'No content available'}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button
            onClick={() => onCopy(message.fullContent || message.preview, message.id)}
            disabled={loadingContent}
            style={{
              ...styles.button,
              ...styles.copyButton,
              ...(isCopied ? styles.copyButtonCopied : {}),
              opacity: loadingContent ? 0.5 : 1,
              cursor: loadingContent ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {isCopied ? <Check size={20} /> : <Copy size={20} />}
            {isCopied ? 'Copied!' : 'Copy Message'}
          </button>
          <button
            onClick={() => onDelete(message.id)}
            style={{
              ...styles.button,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Trash2 size={20} />
            Delete Message
          </button>
          <button
            onClick={onClose}
            style={{
              ...styles.button,
              ...styles.closeButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e2e8f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Saved Messages Page Component
const SavedMessagesPage = () => {
  const [savedMessages, setSavedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [fullMessageContent, setFullMessageContent] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    loadSavedMessages();
  }, []);

  const loadSavedMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/saved-messages`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Loaded saved messages:', data.saved_messages);
      setSavedMessages(data.saved_messages || []);
    } catch (error) {
      console.error('Failed to load saved messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/saved-messages/search?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setSearchResults(data.saved_messages || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchMessageContent = async (message) => {
    // Check if we already have the content cached
    if (fullMessageContent[message.id]) {
      return fullMessageContent[message.id];
    }

    setLoadingContent(true);
    try {
      // Construct S3 URL from s3_bucket and s3_key
      const s3Bucket = process.env.REACT_APP_S3_BUCKET;
      const s3Url = `${s3Bucket}/${message.s3_key}`;
      const response = await fetch(s3Url);
      if (!response.ok) {
        throw new Error('Failed to fetch message from S3');
      }
      
      const content = await response.text();
      
      // Cache the content
      setFullMessageContent(prev => ({
        ...prev,
        [message.id]: content
      }));
      
      return content;
    } catch (error) {
      console.error('Failed to fetch message content:', error);
      return message.preview || 'Failed to load message content';
    } finally {
      setLoadingContent(false);
    }
  };

  const handleOpenMessage = async (msg) => {
    console.log('Opening message:', msg);
    setSelectedMessage(msg);
    
    // Fetch full content from S3
    const content = await fetchMessageContent(msg);
    
    // Update the selected message with full content
    setSelectedMessage({
      ...msg,
      fullContent: content
    });
  };

  const handleCopy = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setToastMessage('Message copied to clipboard!');
    setShowToast(true);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/saved-messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setToastMessage('Message deleted successfully!');
        setShowToast(true);
        setSelectedMessage(null);
        loadSavedMessages();
      } else {
        alert('Failed to delete message');
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  const styles = {
    container: {
      padding: '2rem 0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#64748b',
    },
    searchContainer: {
      marginBottom: '2rem',
      position: 'relative',
    },
    searchInputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      color: '#64748b',
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      padding: '1rem 1rem 1rem 3rem',
      borderRadius: '12px',
      border: '2px solid #e2e8f0',
      fontSize: '1rem',
      transition: 'all 0.2s',
      outline: 'none',
    },
    searchInputFocus: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
    searchingIndicator: {
      position: 'absolute',
      right: '1rem',
      color: '#667eea',
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    clearButton: {
      position: 'absolute',
      right: '1rem',
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '6px',
      padding: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      color: '#64748b',
      transition: 'all 0.2s',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '3rem',
      color: '#64748b',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    emptyIcon: {
      color: '#cbd5e1',
      marginBottom: '1rem',
    },
    emptyTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.5rem',
    },
    emptyText: {
      fontSize: '1rem',
      color: '#64748b',
    },
    messagesList: {
      display: 'grid',
      gap: '1.5rem',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    },
    messageCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '2px solid #e2e8f0',
      transition: 'all 0.2s',
      cursor: 'pointer',
      position: 'relative',
    },
    messageHeader: {
      marginBottom: '1rem',
    },
    messageTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#1e293b',
      marginBottom: '0.5rem',
    },
    messageMeta: {
      display: 'flex',
      gap: '0.75rem',
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.5rem',
      flexWrap: 'wrap',
    },
    metaBadge: {
      background: '#f1f5f9',
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontWeight: 500,
    },
    messagePreview: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: 1.6,
      marginBottom: '1rem',
    },
    expandButton: {
      width: '100%',
      padding: '0.5rem',
      background: 'transparent',
      border: 'none',
      color: '#667eea',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
  };

  // Determine which messages to display
  const displayedMessages = searchQuery.trim() 
    ? (searchResults !== null ? searchResults : [])
    : savedMessages;

  const showEmptyState = displayedMessages.length === 0;
  const isSearchEmpty = searchQuery.trim() && displayedMessages.length === 0;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading saved messages...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>
          <FolderOpen size={32} />
          Saved Messages
        </h1>
        <p style={styles.subtitle}>
          All your saved AI-generated messages in one place
        </p>
      </div>

      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <div style={styles.searchInputWrapper}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search saved messages..."
            value={searchQuery}
            onChange={(e) => {
              const query = e.target.value;
              setSearchQuery(query);
              if (query.trim().length >= 2) {
                handleSearch(query);
              } else {
                setSearchResults(null);
              }
            }}
            style={styles.searchInput}
            onFocus={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
          {isSearching && (
            <span style={styles.searchingIndicator}>Searching...</span>
          )}
          {searchQuery && !isSearching && (
            <button
              style={styles.clearButton}
              onClick={() => {
                setSearchQuery('');
                setSearchResults(null);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {showEmptyState ? (
        <div style={styles.emptyState}>
          <FolderOpen size={64} style={styles.emptyIcon} />
          <h3 style={styles.emptyTitle}>
            {isSearchEmpty ? 'No messages found' : 'No saved messages yet'}
          </h3>
          <p style={styles.emptyText}>
            {isSearchEmpty 
              ? `No messages match "${searchQuery}"`
              : 'Generate and save messages to see them here'
            }
          </p>
        </div>
      ) : (
        <div style={styles.messagesList}>
          <AnimatePresence>
            {displayedMessages.map((msg) => {
              const isCopied = copiedId === msg.id;
              
              return (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  style={styles.messageCard}
                  onClick={() => handleOpenMessage(msg)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={styles.messageHeader}>
                    <div style={styles.messageTitle}>{msg.title}</div>
                    <div style={styles.messageMeta}>
                      <span style={styles.metaBadge}>{msg.contact.name}</span>
                      <span style={styles.metaBadge}>{msg.medium}</span>
                      <span style={styles.metaBadge}>{msg.message_type.replace('_', ' ')}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                      {new Date(msg.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>

                  <div style={styles.messagePreview}>
                    {msg.preview}
                  </div>
                  
                  <button
                    style={styles.expandButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    View Full Message →
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Message Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <MessageModal
            message={selectedMessage}
            onClose={() => setSelectedMessage(null)}
            onCopy={handleCopy}
            onDelete={handleDelete}
            isCopied={copiedId === selectedMessage.id}
            loadingContent={loadingContent}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Subscription Page Component
const SubscriptionPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscription();
    
    // ADD THIS: Handle Stripe redirect parameters
    const searchParams = new URLSearchParams(location.search);
    
    // Success from premium subscription
    if (searchParams.get('payment_success') === 'true') {
      setToastMessage('🎉 Premium subscription activated! Welcome to Loop Premium!');
      setShowToast(true);
      // Clean URL
      setTimeout(() => {
        navigate('/dashboard/settings', { replace: true });
      }, 100);
    }
    
    // Cancelled payment
    if (searchParams.get('payment_cancelled') === 'true') {
      setToastMessage('Payment was cancelled. You can upgrade anytime!');
      setShowToast(true);
      setTimeout(() => {
        navigate('/dashboard/settings', { replace: true });
      }, 100);
    }
    
    // Success from one-time credit purchase
    if (searchParams.get('payment') === 'success') {
      const feature = searchParams.get('feature');
      setToastMessage(`✅ Credits purchased! You can now use ${feature}.`);
      setShowToast(true);
      setTimeout(() => {
        navigate('/dashboard/settings', { replace: true });
      }, 100);
    }
    
    // Cancelled credit purchase
    if (searchParams.get('payment') === 'cancelled') {
      setToastMessage('Purchase was cancelled.');
      setShowToast(true);
      setTimeout(() => {
        navigate('/dashboard/settings', { replace: true });
      }, 100);
    }
    
    // Auto-upgrade trigger (from registration)
    if (searchParams.get('upgrade') === 'true') {
      // Automatically trigger upgrade
      handleUpgrade();
    }
  }, [location]);


  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        credentials: 'include',
      });
      const data = await response.json();
      setSubscription(data.subscription);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your premium subscription? You\'ll keep access until the end of your billing period.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setToastMessage('Subscription cancelled. You\'ll have access until ' + new Date(data.period_end).toLocaleDateString());
        setShowToast(true);
        loadSubscription();
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
  };

  const styles = {
    container: {
      padding: '2rem 0',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#64748b',
    },
    currentPlanCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '2px solid #667eea',
      marginBottom: '2rem',
      maxWidth: '600px',
      margin: '0 auto 2rem auto',
    },
    plansContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem',
      maxWidth: '900px',
      margin: '0 auto',
    },
    planCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s',
      border: '2px solid transparent',
    },
    planCardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
    },
    premiumCard: {
      border: '2px solid #667eea',
    },
    planHeader: {
      marginBottom: '1.5rem',
    },
    planName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '0.5rem',
    },
    planPrice: {
      fontSize: '2.5rem',
      fontWeight: 800,
      color: '#667eea',
      marginBottom: '0.25rem',
    },
    planPeriod: {
      fontSize: '1rem',
      color: '#64748b',
    },
    planDescription: {
      fontSize: '0.95rem',
      color: '#64748b',
      marginBottom: '2rem',
      lineHeight: '1.6',
    },
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 2rem 0',
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 0',
      fontSize: '0.95rem',
      color: '#334155',
    },
    checkIcon: {
      color: '#10b981',
      flexShrink: 0,
    },
    button: {
      width: '100%',
      padding: '1rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    freeButton: {
      background: '#f1f5f9',
      color: '#64748b',
    },
    premiumButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    cancelButton: {
      background: '#fee',
      color: '#dc2626',
      marginTop: '1rem',
    },
    currentPlanBadge: {
      display: 'inline-block',
      background: '#10b981',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: 600,
      marginLeft: '0.5rem',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          Loading subscription...
        </div>
      </div>
    );
  }

  const isPremium = subscription && subscription.plan_type === 'premium';
  const isCancelled = subscription && subscription.cancelled_at;

  return (
    <div style={styles.container}>
      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>Manage Your Subscription</h1>
        <p style={styles.subtitle}>
          {isPremium ? 'You have full access to all premium features' : 'Upgrade to unlock all features'}
        </p>
      </div>

      {/* Current Plan Status */}
      {subscription && (
        <div style={styles.currentPlanCard}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', color: '#1e293b' }}>
            Current Plan: {isPremium ? 'Premium' : 'Free'}
          </h3>
          {isPremium && (
            <>
              <div style={{ fontSize: '0.95rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Status: <strong style={{ color: isCancelled ? '#dc2626' : '#10b981' }}>
                  {isCancelled ? 'Cancelled' : 'Active'}
                </strong>
              </div>
              {subscription.current_period_end && (
                <div style={{ fontSize: '0.95rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  {isCancelled ? 'Access until: ' : 'Renews on: '}
                  <strong>{new Date(subscription.current_period_end).toLocaleDateString()}</strong>
                </div>
              )}
              {!isCancelled && (
                <button
                  onClick={handleCancel}
                  style={{ ...styles.button, ...styles.cancelButton }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fee'}
                >
                  Cancel Subscription
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Plan Cards - Only show if not premium or if cancelled */}
      {(!isPremium || isCancelled) && (
        <div style={styles.plansContainer}>
          {/* Free Plan */}
          <div
            style={{
              ...styles.planCard,
              ...(hoveredPlan === 'free' ? styles.planCardHover : {}),
            }}
            onMouseEnter={() => setHoveredPlan('free')}
            onMouseLeave={() => setHoveredPlan(null)}
          >
            <div style={styles.planHeader}>
              <h2 style={styles.planName}>
                Free
                {!isPremium && <span style={styles.currentPlanBadge}>Current Plan</span>}
              </h2>
              <div style={styles.planPrice}>$0</div>
              <div style={styles.planPeriod}>forever</div>
            </div>
            <p style={styles.planDescription}>Perfect for getting started with Loop</p>
            <ul style={styles.featuresList}>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Contact management
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                AI message generation
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Basic features
              </li>
            </ul>
            <button
              style={{ ...styles.button, ...styles.freeButton }}
              disabled
            >
              Current Plan
            </button>
          </div>

          {/* Premium Plan */}
          <div
            style={{
              ...styles.planCard,
              ...styles.premiumCard,
              ...(hoveredPlan === 'premium' ? styles.planCardHover : {}),
            }}
            onMouseEnter={() => setHoveredPlan('premium')}
            onMouseLeave={() => setHoveredPlan(null)}
          >
            <div style={styles.planHeader}>
              <h2 style={styles.planName}>Premium</h2>
              <div style={styles.planPrice}>$15</div>
              <div style={styles.planPeriod}>per month</div>
            </div>
            <p style={styles.planDescription}>
              Unlock the full power of Loop for serious networkers
            </p>
            <ul style={styles.featuresList}>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Everything in Free
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Unlimited call transcriptions
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Unlimited news schedulers
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Unlimited topic briefings
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Priority support
              </li>
              <li style={styles.feature}>
                <Check size={20} style={styles.checkIcon} />
                Early access to new features
              </li>
            </ul>
            <button
              onClick={handleUpgrade}
              style={{ ...styles.button, ...styles.premiumButton }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Section component
const Section = ({ id, title, icon: Icon, children, isExpanded, onToggle, styles }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={styles.section}>
      <div
        style={{
          ...styles.sectionHeader,
          ...(isHovered ? styles.sectionHeaderHover : {}),
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.sectionTitle}>
          <Icon size={20} />
          {title}
        </div>
        <div style={{
          ...styles.chevron,
          transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        }}>
          <ChevronDown size={20} />
        </div>
      </div>
      {isExpanded && (
        <div style={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
};

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    current_role: '',
    current_company: '',
    industry: '',
    years_experience: '',
    linkedin_url: '',
    education: '',
    location: '',
    communication_style: 'professional',
    tone_preference: 'warm',
    signature_style: '',
    professional_interests: '',
    expertise_areas: '',
    hobbies: '',
    networking_goals: '',
    value_proposition: '',
    current_projects: '',
    fun_facts: '',
    personal_attribues: '',
  });

  const [expandedSections, setExpandedSections] = useState({
    professional: true,
    communication: false,
    expertise: false,
    goals: false,
    personal_attribues: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [originalProfile, setOriginalProfile] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    // Check if profile has changes compared to original
    if (originalProfile) {
      const changed = JSON.stringify(profile) !== JSON.stringify(originalProfile);
      setHasChanges(changed);
    }
  }, [profile, originalProfile]);

  const fetchProfile = async () => {
    try {
      const data = await profileService.getProfile();
      if (data.profile) {
        setProfile(data.profile);
        setOriginalProfile(data.profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileService.updateProfile(profile);
      setOriginalProfile(profile);
      setShowToast(true);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const styles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '2rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#64748b',
    },
    form: {
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    section: {
      borderBottom: '1px solid #e2e8f0',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1.5rem 2rem',
      cursor: 'pointer',
      transition: 'background 0.2s',
      userSelect: 'none',
    },
    sectionHeaderHover: {
      background: '#f8fafc',
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#334155',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    sectionContent: {
      padding: '0 2rem 2rem 2rem',
    },
    inputGroup: {
      marginBottom: '1.25rem',
    },
    label: {
      display: 'block',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#475569',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '1rem',
      transition: 'all 0.2s',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '1rem',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      boxSizing: 'border-box',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1.25rem',
    },
    button: {
      padding: '0.875rem 2rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, opacity 0.2s',
      width: '93%',
      margin: '2rem',
      marginTop: '0',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    helpText: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.25rem',
    },
    chevron: {
      transition: 'transform 0.2s',
    },
  };

  if (loading) {
    return <div style={styles.container}>Loading profile...</div>;
  }

  return (
    <div style={styles.container}>
      {showToast && (
        <Toast
          message="Profile saved successfully! 🎉"
          onClose={() => setShowToast(false)}
        />
      )}

      <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'profile' ? (
        <>
          <div style={styles.header}>
            <h1 style={styles.title}>
              <User size={32} />
              My Profile
            </h1>
            <p style={styles.subtitle}>
              Help Loop understand you better to create more personalized messages
            </p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Professional Information */}
            <Section 
              id="professional" 
              title="Professional Information" 
              icon={Briefcase}
              isExpanded={expandedSections.professional}
              onToggle={() => toggleSection('professional')}
              styles={styles}
            >
              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Current Role</label>
                  <input
                    type="text"
                    name="current_role"
                    value={profile.current_role}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Current Company</label>
                  <input
                    type="text"
                    name="current_company"
                    value={profile.current_company}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Tech Corp"
                  />
                </div>
              </div>
              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={profile.industry}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Technology, Finance"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Years of Experience</label>
                  <input
                    type="number"
                    name="years_experience"
                    value={profile.years_experience}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., 5"
                  />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={profile.linkedin_url}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Education</label>
                  <input
                    type="text"
                    name="education"
                    value={profile.education}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., BS Computer Science, MIT"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={profile.location}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>
              </div>
            </Section>

            {/* Communication Style */}
            <Section 
              id="communication" 
              title="Communication Style" 
              icon={MessageSquare}
              isExpanded={expandedSections.communication}
              onToggle={() => toggleSection('communication')}
              styles={styles}
            >
              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Communication Style</label>
                  <select
                    name="communication_style"
                    value={profile.communication_style}
                    onChange={handleChange}
                    style={styles.select}
                  >
                    <option value="formal">Formal</option>
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="friendly">Friendly</option>
                  </select>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Tone Preference</label>
                  <select
                    name="tone_preference"
                    value={profile.tone_preference}
                    onChange={handleChange}
                    style={styles.select}
                  >
                    <option value="warm">Warm</option>
                    <option value="direct">Direct</option>
                    <option value="enthusiastic">Enthusiastic</option>
                    <option value="thoughtful">Thoughtful</option>
                  </select>
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Signature Style</label>
                <input
                  type="text"
                  name="signature_style"
                  value={profile.signature_style}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="e.g., Best regards, Cheers, Thanks"
                />
                <p style={styles.helpText}>How do you typically sign off your messages?</p>
              </div>
            </Section>

            {/* Expertise & Interests */}
            <Section 
              id="expertise" 
              title="Expertise & Interests" 
              icon={Sparkles}
              isExpanded={expandedSections.expertise}
              onToggle={() => toggleSection('expertise')}
              styles={styles}
            >
              <div style={styles.inputGroup}>
                <label style={styles.label}>Professional Interests</label>
                <textarea
                  name="professional_interests"
                  value={profile.professional_interests}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., AI/ML, blockchain, product management"
                />
                <p style={styles.helpText}>Separate multiple interests with commas</p>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Areas of Expertise</label>
                <textarea
                  name="expertise_areas"
                  value={profile.expertise_areas}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Python, React, team leadership"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Hobbies & Personal Interests</label>
                <textarea
                  name="hobbies"
                  value={profile.hobbies}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., hiking, photography, cooking"
                />
                <p style={styles.helpText}>These can help create more personal connections</p>
              </div>
            </Section>

            {/* Networking Goals */}
            <Section 
              id="goals" 
              title="Networking Goals" 
              icon={Target}
              isExpanded={expandedSections.goals}
              onToggle={() => toggleSection('goals')}
              styles={styles}
            >
              <div style={styles.inputGroup}>
                <label style={styles.label}>What are your networking goals?</label>
                <textarea
                  name="networking_goals"
                  value={profile.networking_goals}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Find mentors in AI, explore job opportunities, build partnerships"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>What value do you bring to your network?</label>
                <textarea
                  name="value_proposition"
                  value={profile.value_proposition}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Technical expertise, industry connections, mentorship"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Current Projects</label>
                <textarea
                  name="current_projects"
                  value={profile.current_projects}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Building an AI startup, learning Spanish, training for a marathon"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Fun Facts About You</label>
                <textarea
                  name="fun_facts"
                  value={profile.fun_facts}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Lived in 5 countries, published author, marathon runner"
                />
                <p style={styles.helpText}>Unique things that make you memorable</p>
              </div>
            </Section>

            {/* Personal Attributes */}
            <Section 
              id="personal_attribues" 
              title="Personal Attributes" 
              icon={Target}
              isExpanded={expandedSections.personal_attribues}
              onToggle={() => toggleSection('personal_attribues')}
              styles={styles}
            >
              <div style={styles.inputGroup}>
                <label style={styles.label}>What is your best attribute?</label>
                <textarea
                  name="personal_attribues"
                  value={profile.personal_attribues}
                  onChange={handleChange}
                  style={styles.textarea}
                  placeholder="e.g., Determination, through years of college sports chasing a common goal with a team has given me teamwork and determination"
                />
              </div>
            </Section>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...((!hasChanges || saving) ? styles.buttonDisabled : {})
              }}
              disabled={!hasChanges || saving}
              onMouseEnter={(e) => {
                if (hasChanges && !saving) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </>
      ) : activeTab === 'subscription' ? (
        <SubscriptionPage />
      ) : (
        <SavedMessagesPage />
      )}
    </div>
  );
};

export default UserProfile;